import React from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Icon,
  IconButton,
  MenuItem,
  TextField,
  Typography
} from '@material-ui/core';
import DateFnsUtils from '@date-io/date-fns';
import { format } from 'date-fns';
import { BackdropCircular } from 'components';
import NumberFormat from 'react-number-format';
import { DatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import CalendarTodayIcon from '@material-ui/icons/CalendarToday';
import { Formik } from 'formik';
import { useStyles } from '../styles';
import { QuoteNotesValidationSchema } from './QuoteNotesValidationSchema';


const dialogTypes = {
  createQuote: {
    title: 'Quote Attributes',
    buttonTitle: 'Create a Quote'
  },
  previewQuote: {
    title: 'Preview Quote Attributes',
    buttonTitle: 'Preview Quote'
  },
  payNow: {
    title: 'Quote Attributes',
    buttonTitle: 'Pay Now'
  }
};

const QuoteNotesDialog = ({
  isOpen,
  onClose,
  onConfirm,
  dialogType,
  total = 0
}) => {
  const classes = useStyles();

  const {
    customerPushCart,
    selectedDraftedQuote,
    draftedQuotes,
    loadingQuotePdfPreview,
    loadingDraftQuotePdfPreview,
    loadingActiveMagentoCart,
    loadingCheckout
  } = useSelector((state: IReduxState) => state.quote);

  let initialExpirationDate = new Date();
  initialExpirationDate.setDate(initialExpirationDate.getDate() + 30);
  const { profile } = useSelector((state: IReduxState) => state.user);
  const activeStoreCode = profile?.stores?.[0].storeCode;
  const currentMonth = new Date().getMonth() + 1;
  const currentYear = new Date().getFullYear();

  if (activeStoreCode === 'drnowak' && currentMonth === 10 && currentYear === 2025) {
    initialExpirationDate = new Date(2025, 9, 31);
  }

  const isLoading = loadingQuotePdfPreview || loadingDraftQuotePdfPreview || loadingActiveMagentoCart || loadingCheckout;

  const selectedDraftQuoteData =  draftedQuotes?.find((quote) => quote._id === selectedDraftedQuote?._id);
  const expirationDate = selectedDraftQuoteData?.expirationDate
    ? selectedDraftQuoteData?.expirationDate
    : format(initialExpirationDate, 'yyyy-MM-dd 23:59:59');

  return (
    <Dialog
      open={isOpen}
      onClose={() => onClose(false)}
      aria-labelledby="quote-notes-dialog"
      PaperProps={{
        className: 'border-radius-6 p-3'
      }}
      fullWidth
      maxWidth={'sm'}
    >
      <DialogTitle id="quote-notes-dialog" className="flex-column w-full p-3">
        <div className="flex justify-between">
          <Typography variant="h5" className={`${classes.modalTitle} mb-0`}>
            {dialogTypes[dialogType].title}
          </Typography>
          <IconButton
            size="small"
            onClick={() => onClose(false)}
          >
            <Icon fontSize="small">close</Icon>
          </IconButton>
        </div>
      </DialogTitle>
      <Formik
        initialValues={{
          quoteName: selectedDraftQuoteData?.name ? selectedDraftQuoteData?.name : customerPushCart?.name || '',
          expirationDate,
          startDate: selectedDraftQuoteData?.startDate || null,
          mustUsedDate: selectedDraftQuoteData?.mustUsedDate || null,
          quoteNotes: selectedDraftQuoteData?.patientNotice || '',
          quoteDiscount: {
            price_type: selectedDraftQuoteData?.quoteDiscount?.price_type || '',
            price: selectedDraftQuoteData?.quoteDiscount?.price || 0
          }
        }}
        validationSchema={QuoteNotesValidationSchema(total)}
        onSubmit={(values) => onConfirm(values)}
      >
        {({
          values: {
            quoteName,
            expirationDate,
            startDate,
            mustUsedDate,
            quoteNotes,
            quoteDiscount: { price, price_type },
          },
          errors,
          touched,
          isValid,
          handleBlur,
          handleSubmit,
          setFieldValue,
          handleChange
        }) => (
          <form onSubmit={handleSubmit} noValidate className="w-full">
            <DialogContent className="p-3" >
              <Grid container spacing={2}>
                {dialogType !== 'payNow' &&
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      name="quoteName"
                      label="Quote Name"
                      variant="outlined"
                      value={quoteName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                  </Grid>
                }

                {dialogType !== 'payNow' &&
                  <MuiPickersUtilsProvider utils={DateFnsUtils}>
                    {selectedDraftQuoteData?.isTreatment &&
                      <Grid item xs={12} sm={4}>
                        <DatePicker
                          autoOk
                          disablePast
                          variant="inline"
                          format="MM-dd-yyyy"
                          name="startDate"
                          placeholder="mm-dd-yyyy"
                          fullWidth
                          className="bg-white"
                          label="Start Date"
                          value={startDate}
                          onChange={(date) => {
                            setFieldValue('startDate', format(date, 'yyyy-MM-dd 00:00:01'));
                          }}
                          onBlur={handleBlur}
                          inputVariant="outlined"
                          InputProps={{
                            endAdornment: (
                              <CalendarTodayIcon className="text-muted ml-2 text-18" />
                            )
                          }}
                        />
                      </Grid>
                    }
                    <Grid item xs={12} sm={selectedDraftQuoteData?.isTreatment ? 4 : 12}>
                      <DatePicker
                        autoOk
                        disablePast
                        disabled={activeStoreCode === 'drnowak' && currentMonth === 10 && currentYear === 2025}
                        variant="inline"
                        format="MM-dd-yyyy"
                        name="expirationDate"
                        placeholder="mm-dd-yyyy"
                        fullWidth
                        className="bg-white"
                        label="Expiration Date"
                        value={expirationDate}
                        onChange={(date) => {
                          setFieldValue('expirationDate', format(date, 'yyyy-MM-dd 23:59:59'));
                        }}
                        onBlur={handleBlur}
                        inputVariant="outlined"
                        InputProps={{
                          endAdornment: (
                            <CalendarTodayIcon className="text-muted ml-2 text-18" />
                          )
                        }}
                      />
                    </Grid>
                    {selectedDraftQuoteData?.isTreatment &&
                      <Grid item xs={12} sm={4}>
                        <DatePicker
                          autoOk
                          disablePast
                          variant="inline"
                          format="MM-dd-yyyy"
                          name="mustUsedDate"
                          placeholder="mm-dd-yyyy"
                          fullWidth
                          className="bg-white"
                          label="Must Used Date"
                          value={mustUsedDate}
                          onChange={(date) => {
                            setFieldValue('mustUsedDate', format(date, 'yyyy-MM-dd 23:59:59'));
                          }}
                          onBlur={handleBlur}
                          inputVariant="outlined"
                          InputProps={{
                            endAdornment: (
                              <CalendarTodayIcon className="text-muted ml-2 text-18" />
                            )
                          }}
                        />
                      </Grid>
                    }
                  </MuiPickersUtilsProvider>
                }
                {dialogType !== 'payNow' &&
                  <Grid item xs={12}>
                    <TextField
                      multiline
                      fullWidth
                      variant="outlined"
                      label="Notes"
                      name="quoteNotes"
                      value={quoteNotes}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.quoteNotes && Boolean(errors.quoteNotes)}
                      helperText={touched.quoteNotes && errors.quoteNotes}
                      inputProps={{
                        style: {
                          padding: 1
                        }
                      }}
                    />
                  </Grid>
                }

                <Grid item xs={6}>
                  <Box className="w-full">
                    <TextField
                      label="Discount Type"
                      select
                      variant="outlined"
                      name="quoteDiscount.price_type"
                      fullWidth
                      value={price_type}
                      onChange={(event) => {
                        const selectedValue = event.target.value;
                        const newPrice = selectedValue === '' ? 0 : price;
                        setFieldValue('quoteDiscount.price_type', selectedValue);
                        setFieldValue('quoteDiscount.price', newPrice);
                      }}
                      onBlur={handleBlur}
                      error={
                        (touched as {
                          quoteDiscount?: { price_type?: boolean };
                        })?.quoteDiscount?.price_type &&
                        Boolean(
                          (errors as {
                            quoteDiscount?: { price_type?: string };
                          })?.quoteDiscount?.price_type
                        )
                      }
                      helperText={
                        (touched as {
                          quoteDiscount?: { price_type?: boolean };
                        })?.quoteDiscount?.price_type &&
                        (errors as {
                          quoteDiscount?: { price_type?: string };
                        })?.quoteDiscount?.price_type
                      }
                    >
                      <MenuItem value="percent">Percentage</MenuItem>
                      <MenuItem value="fixed">Fixed</MenuItem>
                      <MenuItem value="">Not selected</MenuItem>
                    </TextField>
                  </Box>
                </Grid>

                <Grid item xs={6}>
                  <Box className="w-full">
                    <NumberFormat
                      customInput={TextField}
                      variant="outlined"
                      name="quoteDiscount.price"
                      fullWidth
                      label="Discount Value"
                      value={price}
                      onValueChange={(values) => {
                        const { floatValue } = values;
                        handleChange({
                          target: {
                            name: 'quoteDiscount.price',
                            value: floatValue !== undefined ? floatValue : ''
                          },
                        });
                      }}
                      onBlur={handleBlur}
                      disabled={!price_type}
                      suffix={price_type === 'percent' ? '%' : ''}
                      prefix={price_type === 'fixed' ? '$' : ''}
                      fixedDecimalScale
                      decimalScale={2}
                      error={(touched as { quoteDiscount?: { price?: boolean }})?.quoteDiscount?.price &&
                        Boolean(
                          (errors as {
                            quoteDiscount?: { price?: string };
                          })?.quoteDiscount?.price
                        )
                      }
                      helperText={
                        (touched as {
                          quoteDiscount?: { price?: boolean };
                        })?.quoteDiscount?.price &&
                        (errors as {
                          quoteDiscount?: { price?: string };
                        })?.quoteDiscount?.price
                      }
                    />
                  </Box>
                </Grid>
              </Grid>

            </DialogContent>
            <DialogActions className="justify-between">
              <Button
                variant="outlined"
                color="primary"
                onClick={onClose}
                className="w-120 uppercase"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={!isValid}
                className="uppercase"
              >
                {dialogTypes[dialogType].buttonTitle}
              </Button>
            </DialogActions>

            <BackdropCircular loading={isLoading} />
          </form>
        )}
      </Formik>
    </Dialog>
  );
};


export default QuoteNotesDialog;
